import React, { useEffect, useMemo, useState } from 'react';
import { X, GitBranch, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../../component-styles/RecordPages.css';
import API_BASE from '../../../utils/api.js';
import { documentLabel } from '../../../utils/documentTitle.js';

function getApiBase() {
  return API_BASE;
}

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function getRetainerApplicationsForDoc(retainer, docType, docId) {
  const normalizedDocId = String(docId || '');
  const matchedAppliedDocs = safeArray(retainer?.appliedDocs).filter(
    (doc) => doc?.docType === docType && String(doc?.docId || '') === normalizedDocId
  );

  const matchedHistory = safeArray(retainer?.paymentHistory).filter((entry) => {
    if (entry?.paymentType !== 'application') return false;

    const historyDocType = entry?.appliedTo?.docType || entry?.targetType || '';
    const historyDocId = entry?.appliedTo?.docId || entry?.targetId || '';
    return historyDocType === docType && String(historyDocId) === normalizedDocId;
  });

  return [...matchedAppliedDocs, ...matchedHistory];
}

function getListClass(items) {
  const count = Array.isArray(items) ? items.length : 0;
  return `folio-applied-list${count > 6 ? ' scrollable' : ''}`;
}

function getInvoiceProposalId(invoice) {
  return (
    invoice?.proposalId ||
    invoice?.sourceProposalId ||
    invoice?.source?.proposalId ||
    invoice?.meta?.proposalId ||
    ''
  );
}

export default function ProposalFolioModal({ proposal, projectId, onClose }) {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [credits, setCredits] = useState([]);
  const [retainerApps, setRetainerApps] = useState([]);

  useEffect(() => {
    if (!proposal?.proposalId || !userId || !projectId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    const API_BASE = getApiBase();

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [invRes, poRes, creditsRes, retRes] = await Promise.all([
          fetch(
            `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
            { cache: 'no-store' }
          ),
          fetch(
            `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/${encodeURIComponent(
              projectId
            )}`,
            { cache: 'no-store' }
          ),
          fetch(`${API_BASE}/api/credits/${encodeURIComponent(projectId)}`, {
            headers: { userid: userId },
          }),
          fetch(`${API_BASE}/api/retainers/${encodeURIComponent(projectId)}`, {
            headers: { userid: userId },
          }),
        ]);

        const invJson = invRes.ok ? await invRes.json() : [];
        const poJson = poRes.ok ? await poRes.json() : [];
        const creditsJson = creditsRes.ok ? await creditsRes.json() : [];
        const retJson = retRes.ok ? await retRes.json() : [];

        const proposalId = String(proposal.proposalId);
        const directInvoiceIds = new Set(
          [proposal.convertedInvoiceId, ...(Array.isArray(proposal.convertedInvoiceIds) ? proposal.convertedInvoiceIds : [])]
            .filter(Boolean)
            .map((id) => String(id))
        );

        const matchedInvoices = safeArray(invJson).filter(
          (inv) =>
            String(getInvoiceProposalId(inv)) === proposalId || directInvoiceIds.has(String(inv.invoiceId))
        );

        const matchedPOs = safeArray(poJson).filter((po) => {
          const sourceId =
            po.proposalId ||
            po.sourceProposalId ||
            po.source?.proposalId ||
            po.meta?.proposalId ||
            '';
          return String(sourceId) === proposalId;
        });

        const appliedCredits = safeArray(creditsJson)
          .map((credit) => {
            const applied = safeArray(credit.appliedDocs).filter(
              (doc) => doc.docType === 'proposal' && String(doc.docId) === proposalId
            );
            if (applied.length === 0) return null;
            const totalApplied = applied.reduce((s, d) => s + Number(d.amount || 0), 0);
            return {
              creditId: credit.creditId,
              creditNumber: credit.creditNumber || credit.creditId,
              totalApplied,
            };
          })
          .filter(Boolean);

        const retainerApplications = safeArray(retJson)
          .map((ret) => {
            const applied = getRetainerApplicationsForDoc(ret, 'proposal', proposalId);
            if (applied.length === 0) return null;
            const totalApplied = applied.reduce((s, record) => s + Number(record.amount || 0), 0);
            return {
              retainerId: ret.retainerId,
              retainerNumber: ret.retainerNumber || ret.retainerId,
              totalApplied,
            };
          })
          .filter(Boolean);

        if (!mounted) return;
        setInvoices(matchedInvoices);
        setPurchaseOrders(matchedPOs);
        setCredits(appliedCredits);
        setRetainerApps(retainerApplications);
      } catch (err) {
        if (mounted) setError(err.message || 'Failed to load proposal folio.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [proposal, projectId, userId]);

  const summaryTotal = formatCurrency(proposal?.totals?.grandTotal);
  const statusLabel = (proposal?.status || 'draft')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const creditTotal = useMemo(
    () => credits.reduce((s, c) => s + Number(c.totalApplied || 0), 0),
    [credits]
  );
  const retainerTotal = useMemo(
    () => retainerApps.reduce((s, r) => s + Number(r.totalApplied || 0), 0),
    [retainerApps]
  );

  if (!proposal) return null;

  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {documentLabel(proposal.proposalNumber, proposal.title) || 'Proposal'}
          <span className="folio-sub">{proposal.client?.name || '-'}</span>
        </h3>

        <div className="folio-summary">
          <div>
            <label>Status</label>
            <div>{statusLabel}</div>
          </div>
          <div>
            <label>Total</label>
            <div>{summaryTotal}</div>
          </div>
          <div>
            <label>Applied</label>
            <div>{formatCurrency(creditTotal + retainerTotal)}</div>
          </div>
        </div>

        {loading && <div className="folio-empty">Loading folio...</div>}
        {!loading && error && <div className="folio-empty">{error}</div>}

        {!loading && !error && (
          <>
            <h4 className="folio-section-label">Invoices Created ({invoices.length})</h4>
            <div className={getListClass(invoices)}>
              {invoices.length === 0 && (
                <div className="folio-empty">No invoices created from this proposal.</div>
              )}
              {invoices.map((inv) => (
                <Link
                  to={`/projects/${projectId}/invoices/${inv.invoiceId}`}
                  key={inv.invoiceId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{inv.invoiceNumber || inv.invoiceId}</b> — Invoice
                    <span style={{ marginLeft: 6 }}>
                      ({formatCurrency(inv.totals?.total || inv.totals?.grandTotal)})
                    </span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>

            <h4 className="folio-section-label">
              Purchase Orders Created ({purchaseOrders.length})
            </h4>
            <div className={getListClass(purchaseOrders)}>
              {purchaseOrders.length === 0 && (
                <div className="folio-empty">No purchase orders linked yet.</div>
              )}
              {purchaseOrders.map((po) => (
                <Link
                  to={`/projects/${projectId}/purchase-orders/${po.poId}`}
                  key={po.poId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{po.orderNumber || po.poId}</b> — Purchase Order
                    <span style={{ marginLeft: 6 }}>{formatCurrency(po.total)}</span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>

            <h4 className="folio-section-label">Credits Applied ({credits.length})</h4>
            <div className={getListClass(credits)}>
              {credits.length === 0 && <div className="folio-empty">No credits applied.</div>}
              {credits.map((c) => (
                <Link
                  to={`/projects/${projectId}/credits`}
                  key={c.creditId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{c.creditNumber}</b> — Credit
                    <span style={{ marginLeft: 6 }}>{formatCurrency(c.totalApplied)}</span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>

            <h4 className="folio-section-label">
              Retainer Applications ({retainerApps.length})
            </h4>
            <div className={getListClass(retainerApps)}>
              {retainerApps.length === 0 && (
                <div className="folio-empty">No retainers applied to this proposal.</div>
              )}
              {retainerApps.map((r) => (
                <Link
                  to={`/projects/${projectId}/retainers/${r.retainerId}`}
                  key={r.retainerId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{r.retainerNumber}</b> — Retainer
                    <span style={{ marginLeft: 6 }}>{formatCurrency(r.totalApplied)}</span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
